import { Textarea } from '@chakra-ui/react';
import type { CSSProperties } from 'react';

import { globeImage } from '@revelio/assets';
import { Divider, Text } from '@revelio/ui';

import { useBrand } from '../../../../shared/branding/brand-provider';
import {
  DEFAULT_GRID_ROWS,
  REPORT_PAGE_WIDTH,
  REPORT_THEME,
} from '../PageConfigProvider/constants';
import { useTabContentConfig } from '../PageConfigProvider/PageConfigProvider';
import styles from './report-cover-page.module.css';
import { useCoverSelectedParameters } from './useCoverSelectedParameters';

type ReportCoverPageProps = {
  title: string;
  description: string;
  isEditMode?: boolean;
};

export const ReportCoverPage = ({
  title,
  description,
  isEditMode = false,
}: ReportCoverPageProps) => {
  const brand = useBrand();
  const titleColor = brand.coverTitleColor;
  const descriptionColor = brand.coverDescriptionColor;
  const { dispatch } = useTabContentConfig();

  const handleTitleChange = (newTitle: string) => {
    dispatch({ type: 'UPDATE_REPORT_TITLE', payload: { title: newTitle } });
  };

  const handleDescriptionChange = (newDescription: string) => {
    dispatch({
      type: 'UPDATE_REPORT_DESCRIPTION',
      payload: { description: newDescription },
    });
  };
  const topPercent = (8 / DEFAULT_GRID_ROWS) * 100;

  const halfWidth = REPORT_PAGE_WIDTH / 2;
  const reportCenterPadding = `max(calc(50% - ${halfWidth}px), ${REPORT_THEME.margins.minCenterMargin}px)`;

  const hasCoverImage = !!brand.coverImagePortrait;

  return (
    <div
      className={styles.coverPage}
      data-cover-page="true"
      data-has-cover-image={hasCoverImage || undefined}
      style={
        {
          '--report-cover-gradient-start': brand.sidebarColor[900],
          '--report-cover-gradient-end': brand.sidebarColor[500],
          '--report-cover-background': brand.coverBackgroundColor,
          '--report-cover-title-color': titleColor,
          '--report-cover-description-color': descriptionColor,
          '--report-center-padding': reportCenterPadding,
          '--report-cover-logo-top': `${REPORT_THEME.margins.pdf}px`,
          '--report-cover-content-top': `${topPercent}%`,
        } as CSSProperties
      }
    >
      {hasCoverImage ? (
        <img
          className={styles.coverImage}
          src={brand.coverImagePortrait}
          alt={`${brand.displayName} cover`}
          loading="eager"
        />
      ) : (
        <img
          className={styles.globeImage}
          src={globeImage}
          alt="Report Cover"
          loading="eager"
        />
      )}
      <img
        className={styles.logo}
        src={brand.logos.reportLogo}
        alt={`${brand.displayName} logo`}
        loading="eager"
      />
      <div className={styles.content}>
        {title !== undefined &&
          (isEditMode ? (
            <input
              type="text"
              className={styles.titleInput}
              value={title || ''}
              onChange={(e) => handleTitleChange(e.target.value)}
              placeholder="Title"
            />
          ) : (
            <Text
              as="h1"
              fontSize="x-lg"
              weight="bold"
              tone="inherit"
              className={styles.title}
            >
              {title || 'Title'}
            </Text>
          ))}
        {description !== undefined &&
          (isEditMode ? (
            <Textarea
              value={description || ''}
              onChange={(e) => handleDescriptionChange(e.target.value)}
              variant="unstyled"
              fontSize="md"
              color={descriptionColor}
              padding={0}
              lineHeight="1.5"
              sx={{
                '&::placeholder': {
                  color: 'rgba(255, 255, 255, 0.5)',
                },
                '&:focus': {
                  border: '1px solid rgba(255, 255, 255, 0.5)',
                  boxShadow: 'none',
                },
                '&:hover': {
                  border: '1px solid rgba(255, 255, 255, 0.3)',
                },
              }}
              placeholder="Description"
              marginTop={title !== undefined ? '0.5rem' : '0'}
              rows={6}
              resize="vertical"
            />
          ) : (
            <Text
              fontSize="md"
              className={styles.description}
              data-titled={title !== undefined || undefined}
            >
              {description || 'Description'}
            </Text>
          ))}
        <CoverPageParameterSelections />
      </div>
    </div>
  );
};

const CoverPageParameterSelections = () => {
  const parameters = useCoverSelectedParameters();

  const mid = Math.ceil(parameters.length / 2);
  const leftColumn = parameters.slice(0, mid);
  const rightColumn = parameters.slice(mid);

  return (
    <div className={styles.parameterColumns}>
      <div className={styles.parameterColumn}>
        {leftColumn.map((param) => (
          <div
            key={param.label}
            className={styles.leftParameterItem}
            data-spaced={rightColumn.length > 0 || undefined}
          >
            <Text fontSize="sm" weight="bold" className={styles.parameterLabel}>
              {param.label}
            </Text>
            <Text fontSize="sm" className={styles.parameterValue}>
              {param.value}
            </Text>
          </div>
        ))}
      </div>
      {rightColumn.length > 0 && (
        <>
          <Divider className={styles.columnRule} orientation="vertical" />
          <div className={styles.parameterColumn}>
            {rightColumn.map((param) => (
              <div className={styles.parameterItem} key={param.label}>
                <Text
                  fontSize="sm"
                  weight="bold"
                  className={styles.parameterLabel}
                >
                  {param.label}
                </Text>
                <Text fontSize="sm" className={styles.parameterValue}>
                  {param.value}
                </Text>
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
};
